import React, { useState } from 'react';
import { LoggerService } from '../../shared/services/logger.service';
import { AlertasSettings } from '../../pages/dashboard/components/AlertasSettings';

interface SettingsState {
  // Notificaciones
  notificacionesEmail: boolean;
  notificacionesPush: boolean;
  mensajesSMS: boolean;
  
  // Reportes Automáticos
  reporteSemanal: boolean;
  reporteMensual: boolean;
  alertasPresupuesto: boolean;
  actualizacionesInversiones: boolean;
  
  // Privacidad y Datos
  perfilPublico: boolean;
  compartirDatos: boolean;
  analytics: boolean;
}

export const UserSettings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsState>({
    // Notificaciones
    notificacionesEmail: true,
    notificacionesPush: true,
    mensajesSMS: false,
    
    // Reportes Automáticos
    reporteSemanal: true,
    reporteMensual: true,
    alertasPresupuesto: true,
    actualizacionesInversiones: false,
    
    // Privacidad y Datos
    perfilPublico: false,
    compartirDatos: false,
    analytics: true
  });

  const handleToggle = (setting: keyof SettingsState) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
    
    LoggerService.info('Setting toggled', 'UserSettings', { 
      setting, 
      newValue: !settings[setting] 
    });
  };

  const ToggleSwitch: React.FC<{ 
    enabled: boolean; 
    onToggle: () => void;
    size?: 'small' | 'normal';
  }> = ({ enabled, onToggle, size = 'normal' }) => {
    const sizeClasses = size === 'small' 
      ? 'w-8 h-4' 
      : 'w-11 h-6';
    const circleClasses = size === 'small' 
      ? 'w-3 h-3' 
      : 'w-5 h-5';
    const translateClasses = size === 'small' 
      ? (enabled ? 'translate-x-4' : 'translate-x-0')
      : (enabled ? 'translate-x-5' : 'translate-x-0');

    return (
      <button
        onClick={onToggle}
        className={`${sizeClasses} rounded-full p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
          enabled ? 'bg-emerald-600' : 'bg-gray-300'
        }`}
      >
        <div
          className={`${circleClasses} bg-white rounded-full shadow transform transition-transform ${translateClasses}`}
        />
      </button>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
        <p className="text-gray-600 mt-1">Personaliza tu experiencia y preferencias</p>
      </div>

      {/* Notificaciones */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <i className="ri-notification-3-line text-blue-600"></i>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Notificaciones</h2>
        </div>

        <div className="space-y-6">
          {/* Email Notifications */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Notificaciones por Email</h3>
              <p className="text-sm text-gray-500">Recibir notificaciones en tu correo electrónico</p>
            </div>
            <ToggleSwitch 
              enabled={settings.notificacionesEmail}
              onToggle={() => handleToggle('notificacionesEmail')}
            />
          </div>

          {/* Push Notifications */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Notificaciones Push</h3>
              <p className="text-sm text-gray-500">Recibir notificaciones en el navegador</p>
            </div>
            <ToggleSwitch 
              enabled={settings.notificacionesPush}
              onToggle={() => handleToggle('notificacionesPush')}
            />
          </div>

          {/* SMS Messages */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Mensajes SMS</h3>
              <p className="text-sm text-gray-500">Recibir alertas importantes por SMS</p>
            </div>
            <ToggleSwitch 
              enabled={settings.mensajesSMS}
              onToggle={() => handleToggle('mensajesSMS')}
            />
          </div>
        </div>
      </div>

      {/* Reportes Automáticos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-medium text-gray-900 mb-6">Reportes Automáticos</h3>

        <div className="space-y-6">
          {/* Weekly Report */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Reporte Semanal</h4>
            </div>
            <ToggleSwitch 
              enabled={settings.reporteSemanal}
              onToggle={() => handleToggle('reporteSemanal')}
            />
          </div>

          {/* Monthly Report */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Reporte Mensual</h4>
            </div>
            <ToggleSwitch 
              enabled={settings.reporteMensual}
              onToggle={() => handleToggle('reporteMensual')}
            />
          </div>

          {/* Budget Alerts */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Alertas de Presupuesto</h4>
            </div>
            <ToggleSwitch 
              enabled={settings.alertasPresupuesto}
              onToggle={() => handleToggle('alertasPresupuesto')}
            />
          </div>

          {/* Investment Updates */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Actualizaciones de Inversiones</h4>
            </div>
            <ToggleSwitch 
              enabled={settings.actualizacionesInversiones}
              onToggle={() => handleToggle('actualizacionesInversiones')}
            />
          </div>
        </div>
      </div>

      {/* Privacidad y Datos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <i className="ri-shield-check-line text-purple-600"></i>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Privacidad y Datos</h2>
        </div>

        <div className="space-y-6">
          {/* Public Profile */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Perfil Público</h3>
              <p className="text-sm text-gray-500">Permitir que otros usuarios vean tu perfil</p>
            </div>
            <ToggleSwitch 
              enabled={settings.perfilPublico}
              onToggle={() => handleToggle('perfilPublico')}
            />
          </div>

          {/* Share Data */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Compartir Datos</h3>
              <p className="text-sm text-gray-500">Compartir datos anónimos para mejorar el servicio</p>
            </div>
            <ToggleSwitch 
              enabled={settings.compartirDatos}
              onToggle={() => handleToggle('compartirDatos')}
            />
          </div>

          {/* Analytics */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Analytics</h3>
              <p className="text-sm text-gray-500">Permitir análisis de uso para mejorar la experiencia</p>
            </div>
            <ToggleSwitch 
              enabled={settings.analytics}
              onToggle={() => handleToggle('analytics')}
            />
          </div>
        </div>
      </div>

      {/* Alertas de Gastos */}
      <AlertasSettings />
    </div>
  );
};